import { AnyAction, PayloadAction, combineReducers, createSlice } from '@reduxjs/toolkit';
import { ApiError } from '../../../services/api';
import { fetchProjects } from './projects.thunks';
import { BlockedByFeature, BlockedByFeatureType, notAvailablePropExceptIn } from '../../../featureToggles/features/utils';
import { Features } from '../../../featureToggles/features';
import { IRawProject, IProjectList, ISetProjectsResponse } from './projects.interfaces';
import { RootState } from '../../rootReducer';
import CampaignsReducer from './campaigns/campaigns';

// TODO: Remove this entire file when Features.BillingUserFacing is approved in staging and prod
export interface IFiltersProjects {
  keyValue?: string;
  isProjectId?: boolean;
}

export interface IBaseProjectState {
  projectNameList: IProjectList;
  filters?: IFiltersProjects;
  loading: boolean;
  error: ApiError | null;
}

export type IProjectsState = IBaseProjectState | BlockedByFeatureType;

export const initialState: IProjectsState = notAvailablePropExceptIn({
  expected: {
    error: null,
    loading: false,
    projectNameList: {}
  },
  availableIn: [Features.PlgExternal3]
});

const getProjectsState = (state: Pick<RootState, 'accounts'>): IBaseProjectState | undefined => {
  const projectsState: IProjectsState = state.accounts.projects.list;
  if (projectsState === BlockedByFeature) {
    return;
  }
  return projectsState;
};
const getProjectNameList = (state: Pick<RootState, 'accounts'>): IProjectList => getProjectsState(state)?.projectNameList || {};
const getProjectFilters = (state: Pick<RootState, 'accounts'>): IFiltersProjects | undefined => getProjectsState(state)?.filters;

export const projectsSelector = {
  getProjectNameList,
  getProjectFilters
};

const reducers = {
  [fetchProjects.pending.type]: (state: IProjectsState): IProjectsState => {
    if (state === BlockedByFeature) {
      return state;
    }
    state.loading = true;
    state.error = null;
    return state;
  },
  [fetchProjects.fulfilled.type]: (state: IProjectsState, action: PayloadAction<ISetProjectsResponse>): IProjectsState => {
    if (state === BlockedByFeature) {
      return state;
    }
    state.loading = false;
    const docs: IRawProject[] = action.payload?.docs ?? [];
    state.projectNameList = docs.reduce((projects: IProjectList, project: IRawProject) => {
      projects[project._id] = {
        name: project.projectName
      };
      return projects;
    }, {});
    return state;
  },
  [fetchProjects.rejected.type]: (state: IProjectsState, action: AnyAction): IProjectsState => {
    if (state === BlockedByFeature) {
      return state;
    }
    state.loading = false;
    state.error = action.payload;
    return state;
  }
};

const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  extraReducers: reducers,
  reducers: {
    clearProjects: (state: IProjectsState) => {
      if (state === BlockedByFeature) {
        return state;
      }
      state = {
        error: null,
        loading: false,
        projectNameList: {}
      };
      return state;
    },
    setFilters: (state: IProjectsState, action: PayloadAction<IFiltersProjects | undefined>) => {
      if (state === BlockedByFeature) {
        return state;
      }
      state.filters = action.payload;
      return state;
    }
  }
});

export default projectsSlice.reducer;
export const actions = {
  ...projectsSlice.actions
};

export const projectsReducer = combineReducers({
  list: projectsSlice.reducer,
  campaigns: CampaignsReducer
});
